import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDownLeft, ArrowUpRight, Loader2, Pencil, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import EmailEditor from '@/client/components/admin/Email/EmailEditor';
import { useMailboxesQuery } from '@/client/api/inbox/inbox';
import { useMediaOutletsQuery } from '@/client/api/marketing/sources';
import {
  useContentItemQuery,
  useCreatePlacementMutation,
  useContentVariantsQuery,
  useCreateOutletEmailMutation,
  useDeleteOutletEmailMutation,
  useOutletEmailThreadQuery,
  useOutletEmailsQuery,
  usePlacementsQuery,
  useTransitionOutletEmailMutation,
  useUpdateOutletEmailMutation,
} from '@/client/api/marketing/studio';
import type {
  ApiMediaOutlet,
  ApiOutletEmail,
  ApiPlacement,
  OutletEmailKind,
  OutletEmailStatus,
} from '@/client/types/marketing/api';
import { FieldHint, RequiredMark, fieldClass } from '@/client/pages/Admin/shared/form-field';
import { getApiErrorMessage } from '@/client/utils/errors';
import { ActionIconButton } from './ActionIconButton';
import { StatusBadge } from './marketing-ui';

/**
 * Email an outlet about one placement: ask for a quote, pitch, deliver the
 * article, follow up.
 *
 * Nothing here sends. Every email is saved as a draft and submitted; approving
 * it in Crew → Approvals is what sends it, from a shared mailbox, so the
 * outlet's reply comes back to the Communications inbox and shows in the thread
 * below. Opened without a placement (from the Press queue header or an outlet
 * row), it asks for the outlet if none was given and creates a draft placement
 * on the first save, so cancelling leaves nothing behind.
 *
 * The article can only be submitted once the placement itself is
 * approved: delivering it means the deal is agreed.
 */

export const OUTLET_EMAIL_KIND_LABEL: Record<OutletEmailKind, string> = {
  quote_request: 'Quote request',
  pitch: 'Pitch',
  article: 'Article',
  follow_up: 'Follow-up',
};

export const OUTLET_EMAIL_STATUS_LABEL: Record<OutletEmailStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Awaiting approval',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
};

export const OUTLET_EMAIL_STATUS_BADGE: Record<OutletEmailStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-100 text-amber-800',
  sending: 'bg-sky-100 text-sky-800',
  sent: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-700',
};

const DEFAULT_MAILBOX = 'marketing@feelyfeely.com';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Content bodies are usually editor HTML; a plain-text one becomes paragraphs. */
const asHtml = (body: string) =>
  /<(p|div|h[1-6]|ul|ol)\b/i.test(body)
    ? body
    : body
        .split(/\n{2,}/)
        .map((para) => `<p>${esc(para).replace(/\n/g, '<br>')}</p>`)
        .join('');

interface TemplateInput {
  outlet: string;
  title: string | null;
  excerpt: string | null;
  article: string | null;
}

/** Starting points only; the editor is where the real email gets written. */
function template(kind: OutletEmailKind, t: TemplateInput): { subject: string; body: string } {
  const outlet = esc(t.outlet);
  const title = t.title ? esc(t.title) : null;
  switch (kind) {
    case 'quote_request':
      return {
        subject: `Sponsored article rates on ${t.outlet}`,
        body:
          `<p>Hello ${outlet} team,</p>` +
          `<p>We'd like to publish a sponsored article on ${outlet}. Could you send your current rates, and let us know:</p>` +
          '<ul><li>whether the article stays up permanently</li>' +
          '<li>whether links are marked sponsored or nofollow</li>' +
          '<li>whether it is shared on your social channels</li>' +
          '<li>any package price for three or more articles</li></ul>' +
          '<p>Thank you.</p>',
      };
    case 'pitch':
      return {
        subject: title ? `Story idea: ${t.title}` : `Story idea for ${t.outlet}`,
        body:
          `<p>Hello ${outlet} team,</p>` +
          `<p>${title ? `We have a story your readers may like: <strong>${title}</strong>.` : 'We have a story your readers may like.'}</p>` +
          (t.excerpt ? `<p>${esc(t.excerpt)}</p>` : '') +
          '<p>Happy to share the data behind it, images and a spokesperson. Would it suit you?</p>',
      };
    case 'article':
      return {
        subject: title ? `Article for publication: ${t.title}` : 'Article for publication',
        body:
          `<p>Hello ${outlet} team,</p>` +
          '<p>As agreed, here is the article for publication.</p>' +
          '<hr>' +
          (title ? `<h2>${title}</h2>` : '') +
          (t.article ? asHtml(t.article) : '<p></p>') +
          '<hr><p>Please send the link once it is live.</p>',
      };
    case 'follow_up':
    default:
      return {
        subject: title ? `Following up: ${t.title}` : `Following up`,
        body: `<p>Hello ${outlet} team,</p><p>Following up on my earlier email. Any update?</p>`,
      };
  }
}

interface Draft {
  meid: number | null;
  kind: OutletEmailKind;
  fromMailbox: string;
  toEmail: string;
  toName: string;
  subject: string;
  body: string;
}

type DraftErrors = Partial<Record<'toEmail' | 'subject' | 'body', string>>;

interface Props {
  open: boolean;
  /** Null = start fresh: a draft placement is created on the first save. */
  placement: ApiPlacement | null;
  /** Preset outlet; when absent and there is no placement, the dialog asks. */
  outlet: ApiMediaOutlet | undefined;
  onClose: () => void;
}

export function OutletEmailDialog({
  open,
  placement: placementProp,
  outlet: outletProp,
  onClose,
}: Props) {
  const [created, setCreated] = useState<ApiPlacement | null>(null);
  const [pickedMoid, setPickedMoid] = useState<number | null>(null);
  const outletsQ = useMediaOutletsQuery({ limit: 200 });
  const pitchable = useMemo(
    () =>
      (outletsQ.data?.items ?? [])
        .filter((o) => o.active && o.use_as_destination && !o.do_not_pitch)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [outletsQ.data],
  );
  const outlet =
    outletProp ??
    (outletsQ.data?.items ?? []).find(
      (o) => o.moid === ((placementProp ?? created)?.moid ?? pickedMoid),
    );
  // Opened from an outlet (or after picking one): carry on under its open
  // placement rather than starting a second one, so follow-ups share a thread.
  const placementsQ = usePlacementsQuery({ limit: 200 });
  const reused =
    !placementProp && !created && outlet
      ? (placementsQ.data?.items ?? []).find(
          (p) => p.moid === outlet.moid && !['published', 'failed'].includes(p.status),
        )
      : undefined;
  const placement = placementProp ?? created ?? reused ?? null;
  const needsOutletPick = !placementProp && !outletProp && !created;
  const createPlacement = useCreatePlacementMutation();
  const mtpid = placement?.mtpid ?? null;
  const emailsQ = useOutletEmailsQuery({ mtpid, limit: 50 }, { enabled: open && mtpid != null });
  const mailboxes = useMailboxesQuery().data ?? [];
  const contentQ = useContentItemQuery(placement?.mciid ?? NaN, open && placement?.mciid != null);
  const variantsQ = useContentVariantsQuery(
    placement?.mciid ?? NaN,
    open && placement?.mciid != null,
  );

  const createEmail = useCreateOutletEmailMutation();
  const updateEmail = useUpdateOutletEmailMutation();
  const deleteEmail = useDeleteOutletEmailMutation();
  const transition = useTransitionOutletEmailMutation();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [threadFor, setThreadFor] = useState<number | null>(null);
  const threadQ = useOutletEmailThreadQuery(threadFor);

  const emails: ApiOutletEmail[] = emailsQ.data?.items ?? [];
  const outletName = outlet?.name ?? (placement ? `Outlet ${placement.moid}` : 'Outlet');
  const defaultTo = outlet?.pitch_email || outlet?.contact_email || '';
  const placementApproved =
    !!placement && ['queued', 'scheduled', 'published'].includes(placement.status);

  const templateInput = useMemo<TemplateInput>(() => {
    const content = contentQ.data;
    // The outlet-specific variant, when the editor wrote one, beats the master body.
    const variant = variantsQ.data?.items.find(
      (v) => v.source_kind === 'outlet' && v.moid === placement?.moid && v.body,
    );
    return {
      outlet: outletName,
      title: content?.title ?? null,
      excerpt: content?.excerpt ?? null,
      article: variant?.body ?? content?.body ?? null,
    };
  }, [contentQ.data, variantsQ.data, outletName, placement?.moid]);

  useEffect(() => {
    if (!open) {
      setDraft(null);
      setErrors({});
      setThreadFor(null);
      setCreated(null);
      setPickedMoid(null);
    }
  }, [open]);

  const startNew = (kind: OutletEmailKind) => {
    const t = template(kind, templateInput);
    setErrors({});
    setDraft({
      meid: null,
      kind,
      fromMailbox: mailboxes.some((m) => m.email === DEFAULT_MAILBOX)
        ? DEFAULT_MAILBOX
        : (mailboxes[0]?.email ?? DEFAULT_MAILBOX),
      toEmail: defaultTo,
      toName: outletName,
      subject: t.subject,
      body: t.body,
    });
  };

  const startEdit = (e: ApiOutletEmail) => {
    setErrors({});
    setDraft({
      meid: e.meid,
      kind: e.kind,
      fromMailbox: e.from_mailbox,
      toEmail: e.to_email,
      toName: e.to_name ?? '',
      subject: e.subject,
      body: e.body,
    });
  };

  const patch = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    if (key in errors) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const save = async (andSubmit: boolean) => {
    if (!draft || !outlet) return;
    const next: DraftErrors = {};
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.toEmail.trim())) {
      next.toEmail = 'Enter the address to send to';
    }
    if (!draft.subject.trim()) next.subject = 'Subject is required';
    if (!draft.body.replace(/<[^>]+>/g, '').trim()) next.body = 'Write the email';
    if (andSubmit && draft.kind === 'article' && !placementApproved) {
      toast.error('Approve the placement before submitting the article');
      return;
    }
    if (Object.keys(next).length) {
      setErrors(next);
      toast.error('Fix the highlighted fields before saving');
      return;
    }

    const payload = {
      kind: draft.kind,
      from_mailbox: draft.fromMailbox,
      to_email: draft.toEmail.trim(),
      to_name: draft.toName.trim() || null,
      subject: draft.subject.trim(),
      body: draft.body,
    };
    const submit = (meid: number) =>
      transition.mutate(
        { meid, action: 'submit' },
        {
          onSuccess: () => {
            toast.success('Submitted. It sends when approved in Crew → Approvals.');
            setDraft(null);
          },
          onError: (err) => toast.error(getApiErrorMessage(err, 'Saved, but not submitted')),
        },
      );
    const onSaved = (meid: number | undefined) => {
      if (andSubmit && meid) submit(meid);
      else {
        toast.success('Draft saved');
        setDraft(null);
      }
    };
    const onError = (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save'));

    let target = placement;
    if (!target) {
      try {
        const res = await createPlacement.mutateAsync({
          moid: outlet.moid,
          medium: outlet.medium,
          notes: 'Created from an outlet email',
        });
        target = res?.data ?? null;
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Could not create the placement'));
        return;
      }
      if (!target) return;
      setCreated(target);
    }

    if (draft.meid) {
      updateEmail.mutate(
        { meid: draft.meid, ...payload },
        { onSuccess: () => onSaved(draft.meid ?? undefined), onError },
      );
    } else {
      createEmail.mutate(
        { mtpid: target.mtpid, ...payload },
        { onSuccess: (res) => onSaved(res?.data?.meid), onError },
      );
    }
  };

  const busy =
    createPlacement.isPending ||
    createEmail.isPending ||
    updateEmail.isPending ||
    transition.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{outlet || placement ? `Email ${outletName}` : 'Email an outlet'}</DialogTitle>
          <DialogDescription>
            Drafts are sent only after approval in{' '}
            <Link to="/admin/marketing/crew/approvals" className="text-blue-700 hover:underline">
              Crew → Approvals
            </Link>
            . Replies arrive in the Communications inbox and show here.
          </DialogDescription>
        </DialogHeader>

        {needsOutletPick && (
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">
              Outlet <RequiredMark />
            </span>
            <select
              value={pickedMoid ?? ''}
              onChange={(e) => {
                setPickedMoid(e.target.value ? Number(e.target.value) : null);
                setDraft(null);
              }}
              className={inputClass}
              disabled={outletsQ.isLoading}
            >
              <option value="">{outletsQ.isLoading ? 'Loading outlets…' : 'Choose an outlet…'}</option>
              {pitchable.map((o) => (
                <option key={o.moid} value={o.moid}>
                  {o.name}
                  {o.price_amount != null
                    ? ` · ${o.price_currency ?? ''} ${(o.price_amount / 100).toLocaleString()}`
                    : ''}
                </option>
              ))}
            </select>
            <span className="block text-[11px] text-gray-400">
              A draft placement for this outlet is created when you first save the email.
            </span>
          </label>
        )}

        {outlet && (outlet.price_amount != null || outlet.self_serve_url) && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            {outlet.price_amount != null && (
              <span>
                Listed price: {outlet.price_currency ?? ''}{' '}
                {(outlet.price_amount / 100).toLocaleString()}
                {outlet.link_policy && outlet.link_policy !== 'unknown'
                  ? ` · links ${outlet.link_policy}`
                  : ''}
                {outlet.rate_checked_at ? ` · checked ${outlet.rate_checked_at}` : ''}
              </span>
            )}
            {outlet.self_serve_url && (
              <a
                href={outlet.self_serve_url}
                target="_blank"
                rel="noreferrer"
                className="ml-2 text-blue-700 hover:underline"
              >
                Self-serve booking
              </a>
            )}
          </div>
        )}

        {/* ----- existing emails ----- */}
        {outlet && (
        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-gray-900">Emails on this placement</h3>
            {!draft && (
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(OUTLET_EMAIL_KIND_LABEL) as OutletEmailKind[]).map((k) => (
                  <Button
                    key={k}
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={() => startNew(k)}
                    disabled={k === 'article' && !placementApproved}
                    title={
                      k === 'article' && !placementApproved
                        ? 'Approve the placement first'
                        : undefined
                    }
                  >
                    {OUTLET_EMAIL_KIND_LABEL[k]}
                  </Button>
                ))}
              </div>
            )}
          </div>
          {!defaultTo && !draft && (
            <p className="text-[11px] text-amber-700">
              This outlet has no pitch or contact email. Add one under Sources → Outlets, or type
              the address in the email.
            </p>
          )}

          {emailsQ.isLoading ? (
            <div className="py-6 text-center text-sm text-gray-500">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : emails.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-xs text-gray-400">
              No emails yet. Start with a quote request or a pitch.
            </p>
          ) : (
            <TooltipProvider delayDuration={200}>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
              {emails.map((e) => {
                const editable = e.status === 'draft' || e.status === 'failed';
                return (
                  <li key={e.meid} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                    <span className="w-24 shrink-0 text-xs text-gray-500">
                      {OUTLET_EMAIL_KIND_LABEL[e.kind]}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-900">{e.subject}</p>
                      <p className="truncate text-[11px] text-gray-400">
                        {e.from_mailbox} → {e.to_email}
                        {e.sent_at ? ` · sent ${new Date(e.sent_at).toLocaleString()}` : ''}
                        {e.fail_reason ? ` · ${e.fail_reason}` : ''}
                      </p>
                    </div>
                    <StatusBadge
                      label={OUTLET_EMAIL_STATUS_LABEL[e.status] ?? e.status}
                      className={OUTLET_EMAIL_STATUS_BADGE[e.status] ?? ''}
                    />
                    {e.status === 'sent' && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 text-xs"
                        onClick={() => setThreadFor(threadFor === e.meid ? null : e.meid)}
                      >
                        {threadFor === e.meid ? 'Hide thread' : 'Thread'}
                      </Button>
                    )}
                    {editable && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1"
                          disabled={busy}
                          onClick={() =>
                            transition.mutate(
                              { meid: e.meid, action: 'submit' },
                              {
                                onSuccess: () => toast.success('Submitted for approval'),
                                onError: (err) =>
                                  toast.error(getApiErrorMessage(err, 'Could not submit')),
                              },
                            )
                          }
                        >
                          <Send className="h-3.5 w-3.5" /> Submit
                        </Button>
                        <ActionIconButton label="Edit" onClick={() => startEdit(e)}>
                          <Pencil className="h-4 w-4" />
                        </ActionIconButton>
                        <ActionIconButton
                          label="Delete"
                          onClick={() =>
                            deleteEmail.mutate(
                              { meid: e.meid },
                              {
                                onSuccess: () => toast.success('Email deleted'),
                                onError: (err) =>
                                  toast.error(getApiErrorMessage(err, 'Could not delete')),
                              },
                            )
                          }
                          className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </ActionIconButton>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            </TooltipProvider>
          )}
        </section>
        )}

        {/* ----- thread ----- */}
        {threadFor != null && (
          <section className="space-y-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
            <h3 className="text-xs font-medium text-gray-600">
              Conversation with {threadQ.data?.address ?? '…'}
            </h3>
            {threadQ.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : (threadQ.data?.messages ?? []).length === 0 ? (
              <p className="text-xs text-gray-400">No messages recorded yet.</p>
            ) : (
              <ul className="space-y-2">
                {threadQ.data!.messages.map((m, i) => (
                  <li
                    key={i}
                    className={`rounded-md px-3 py-2 text-xs ${
                      m.direction === 'inbound'
                        ? 'mr-8 bg-white text-gray-800'
                        : 'ml-8 bg-blue-50 text-gray-800'
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-1 text-[11px] text-gray-400">
                      {m.direction === 'inbound' ? (
                        <ArrowDownLeft className="h-3 w-3" />
                      ) : (
                        <ArrowUpRight className="h-3 w-3" />
                      )}
                      {m.direction === 'inbound' ? 'Reply' : 'Sent'} ·{' '}
                      {new Date(m.at * 1000).toLocaleString()}
                      {m.subject ? ` · ${m.subject}` : ''}
                    </div>
                    <p className="line-clamp-6 whitespace-pre-line">{m.body}</p>
                  </li>
                ))}
              </ul>
            )}
            <Link
              to={`/admin/communications/inbox?channel=email`}
              className="text-[11px] text-blue-700 hover:underline"
            >
              Reply from the inbox →
            </Link>
          </section>
        )}

        {/* ----- composer ----- */}
        {draft && (
          <section className="space-y-3 rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                {draft.meid ? 'Edit email' : 'New email'}
              </h3>
              <select
                value={draft.kind}
                onChange={(e) => patch('kind', e.target.value as OutletEmailKind)}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs"
                aria-label="Email type"
              >
                {(Object.keys(OUTLET_EMAIL_KIND_LABEL) as OutletEmailKind[]).map((k) => (
                  <option key={k} value={k}>
                    {OUTLET_EMAIL_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">From</span>
                <select
                  value={draft.fromMailbox}
                  onChange={(e) => patch('fromMailbox', e.target.value)}
                  className={inputClass}
                >
                  {(mailboxes.length ? mailboxes : [{ email: DEFAULT_MAILBOX, name: '' }]).map(
                    (m) => (
                      <option key={m.email} value={m.email}>
                        {m.name ? `${m.name} <${m.email}>` : m.email}
                      </option>
                    ),
                  )}
                </select>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">
                  To <RequiredMark />
                </span>
                <input
                  type="email"
                  value={draft.toEmail}
                  onChange={(e) => patch('toEmail', e.target.value)}
                  className={fieldClass(inputClass, errors.toEmail)}
                  aria-invalid={!!errors.toEmail}
                  placeholder="ads@outlet.com"
                  list="outlet-email-options"
                />
                {/* Every email on the outlet, so a second address is one click. */}
                <datalist id="outlet-email-options">
                  {[
                    outlet?.pitch_email,
                    ...(outlet?.contact_points ?? [])
                      .filter((p) => p.kind === 'email')
                      .map((p) => p.value),
                    outlet?.contact_email,
                  ]
                    .filter((v, i, a): v is string => !!v && a.indexOf(v) === i)
                    .map((v) => (
                      <option key={v} value={v} />
                    ))}
                </datalist>
                <FieldHint error={errors.toEmail} />
              </label>
            </div>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Subject <RequiredMark />
              </span>
              <input
                value={draft.subject}
                onChange={(e) => patch('subject', e.target.value)}
                className={fieldClass(inputClass, errors.subject)}
                aria-invalid={!!errors.subject}
              />
              <FieldHint error={errors.subject} />
            </label>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Message <RequiredMark />
              </span>
              <div
                className={`overflow-hidden rounded-md border ${errors.body ? 'border-red-400' : 'border-gray-200'}`}
              >
                <EmailEditor
                  value={draft.body}
                  minHeight={220}
                  onChange={(c) => patch('body', c.html)}
                />
              </div>
              <FieldHint error={errors.body} />
              <p className="text-[11px] text-gray-400">
                The mailbox signature is added when it sends.
                {draft.kind === 'article' && !placement?.mciid
                  ? ' Link a content item to the placement to fill the article in.'
                  : ''}
              </p>
            </div>

            <div className="flex flex-wrap justify-between gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const t = template(draft.kind, templateInput);
                  setDraft((d) => (d ? { ...d, subject: t.subject, body: t.body } : d));
                }}
              >
                Reset to template
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setDraft(null)} disabled={busy}>
                  Cancel
                </Button>
                <Button type="button" variant="outline" onClick={() => save(false)} disabled={busy}>
                  Save draft
                </Button>
                <Button type="button" onClick={() => save(true)} disabled={busy} className="gap-1">
                  <Send className="h-3.5 w-3.5" />
                  {busy ? 'Saving…' : 'Submit for approval'}
                </Button>
              </div>
            </div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}
